import React from 'react';
import { ICONS, LEGACY } from '../utils/eventIcons';

// Re-exported so every screen keeps importing all of this from one place.
export { ICONS, iconNames, iconOf, stripIcon, withIcon, ICON_HEADING, aliasesFor } from '../utils/eventIcons';

/**
 * The twenty-four event icons, drawn by us instead of by the phone.
 *
 * Silvia compared emoji against drawings side by side and chose the drawings.
 * The reason is the one that cannot be shown in a comparison sheet: an emoji is
 * a request to the phone's own font, so 🎂 is a different picture on a Samsung,
 * on a Xiaomi and on Windows, and some — 🍽️, 🏖️, 👪 — do not arrive at all on
 * certain devices. A drawing is identical everywhere and takes the colour of
 * the theme; an emoji keeps its own colours for ever.
 *
 * THE PART THAT MATTERS, and the reason an emoji is still in here.
 *
 * An icon lives at the FRONT OF THE TITLE — "🎂 Compleanno di Luca" — and that
 * was a deliberate choice: it means the icon survives everything MeMo does not
 * control. It reaches Google Calendar, the morning email, the shared calendar
 * a friend reads on another app, the exported .ics file. It is part of the
 * name of the thing, not a field MeMo has to remember and carry alongside.
 *
 * A drawing cannot live inside a title: a title is text. So the emoji stays as
 * what is WRITTEN, and the drawing is what is SHOWN. Inside MeMo you see the
 * drawing, identical on every phone; outside MeMo the emoji is still there
 * doing its job. Nothing had to be given up, and no event already saved breaks.
 *
 * `LEGACY` exists for the same reason: Sport used to be 🏃 and is now the
 * barbell. Events saved with 🏃 must keep showing an icon, so the old character
 * is still recognised and drawn as the new picture.
 */

// ── the drawings ────────────────────────────────────────────────────────────
// Every one is a 24×24 line drawing, stroked with currentColor: it inherits
// whatever colour the text around it has, in light and dark theme alike.
const DRAW = {
  // Compleanno — three candles, lit. (Silvia: "ridisegna la torta mettendo 3 candeline")
  '🎂': <>
    <path d="M4 21h16"/>
    <path d="M5.5 21v-6.4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2V21"/>
    <path d="M5.5 17.6c1.1 0 1.1 1.2 2.2 1.2s1.1-1.2 2.2-1.2 1.1 1.2 2.2 1.2 1.1-1.2 2.2-1.2 1.1 1.2 2.2 1.2 1.1-1.2 2.2-1.2"/>
    <path d="M8 12.6V9.4M12 12.6V9.4M16 12.6V9.4"/>
    <path d="M8 6.3c.9.9.9 2 0 2.7-.9-.7-.9-1.8 0-2.7zM12 6.3c.9.9.9 2 0 2.7-.9-.7-.9-1.8 0-2.7zM16 6.3c.9.9.9 2 0 2.7-.9-.7-.9-1.8 0-2.7z"/>
  </>,
  // Dottore — the pharmacy cross. A stethoscope reads as a doctor only to
  // somebody who already knows it is one; a cross reads as health to everyone.
  '🩺': <path d="M9.6 3h4.8a1 1 0 0 1 1 1v4.6H20a1 1 0 0 1 1 1v4.8a1 1 0 0 1-1 1h-4.6V20a1 1 0 0 1-1 1H9.6a1 1 0 0 1-1-1v-4.6H4a1 1 0 0 1-1-1V9.6a1 1 0 0 1 1-1h4.6V4a1 1 0 0 1 1-1z"/>,
  // Veterinario — a paw.
  '🐾': <>
    <circle cx="7" cy="8" r="1.8"/><circle cx="12" cy="6" r="1.8"/><circle cx="17" cy="8" r="1.8"/>
    <path d="M12 11c-3 0-5 2.2-5 4.6C7 18 9 19 12 19s5-1 5-3.4C17 13.2 15 11 12 11z"/>
  </>,
  // Sport — the barbell, which also swallowed the old "Palestra": they were
  // the same thing twice.
  '🏋️': <>
    <path d="M2.5 9.5v5M5.5 6.5v11M18.5 6.5v11M21.5 9.5v5"/>
    <path d="M5.5 12h13"/>
  </>,
  // Lavoro — a briefcase.
  '💼': <>
    <rect x="3" y="7" width="18" height="13" rx="2"/>
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
    <path d="M3 12h18"/>
  </>,
  // Cena — a plate between a fork and a knife. Cutlery on its own floated.
  '🍽️': <>
    <circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="2"/>
    <path d="M2.6 3.4v3.9a1.8 1.8 0 0 0 3.6 0V3.4"/>
    <path d="M4.4 9.1V20.6"/>
    <path d="M19.6 20.6V3.4c1.7 1.5 2.3 4.3 2.3 6.3 0 1.4-1 2.3-2.3 2.3"/>
  </>,
  // Viaggio — an aeroplane seen from above, not a paper dart.
  '✈️': <path d="M12 2.6c1 0 1.7 1.2 1.7 3v3.6l7.3 4.3v2.1l-7.3-2.2v3.5l2.3 1.7v1.7L12 19.4l-4 .9v-1.7l2.3-1.7v-3.5L3 15.6v-2.1l7.3-4.3V5.6c0-1.8.7-3 1.7-3z"/>,
  // Spesa — a trolley.
  '🛒': <>
    <path d="M3 4h2l2.2 10.4a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.6L21 8H6"/>
    <circle cx="10" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/>
  </>,
  // Festa — the popper firing confetti. Two balloons read as nothing.
  '🎉': <>
    <path d="M2.6 21.4L9.2 8.6l6.2 6.2z"/>
    <path d="M13.8 6.4l1.6-1.6M18 9.6l1.8-1.1M16.4 3.4l.6-1.8M19.6 13.4l2-.4"/>
    <circle cx="14.4" cy="11.6" r=".8"/><circle cx="19.8" cy="17.6" r=".8"/>
  </>,
  // Riunione — two people facing each other across a table.
  '👥': <>
    <circle cx="6.4" cy="6.2" r="2.8"/>
    <path d="M2 16.6v-1.3a4.4 4.4 0 0 1 4.4-4.4 4.4 4.4 0 0 1 4.4 4.4v1.3"/>
    <circle cx="17.6" cy="6.2" r="2.8"/>
    <path d="M13.2 16.6v-1.3a4.4 4.4 0 0 1 4.4-4.4 4.4 4.4 0 0 1 4.4 4.4v1.3"/>
    <path d="M1.5 19.6h21"/>
  </>,
  // Scuola — an open book.
  '🎓': <>
    <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v16H5.5A1.5 1.5 0 0 0 4 20.5z"/>
    <path d="M4 17.5A1.5 1.5 0 0 1 5.5 16H19"/>
    <path d="M9 3v13"/>
  </>,
  '🚗': <>
    <path d="M4 16v2.5M20 16v2.5"/>
    <path d="M3 16v-3l2-5h14l2 5v3z"/>
    <circle cx="7.5" cy="16" r="1.5"/><circle cx="16.5" cy="16" r="1.5"/>
    <path d="M5 13h14"/>
  </>,
  '🏠': <>
    <path d="M3 11l9-7 9 7"/><path d="M5.5 9.5V20h13V9.5"/><path d="M10 20v-5h4v5"/>
  </>,
  '📞': <path d="M6.5 3h3l1.5 4-2 1.5a12 12 0 0 0 6.5 6.5L17 13l4 1.5v3a2 2 0 0 1-2.2 2C10.6 18.7 5.3 13.4 4.5 5.2A2 2 0 0 1 6.5 3z"/>,
  '🎵': <>
    <circle cx="7" cy="18" r="2.5"/><circle cx="17" cy="16" r="2.5"/>
    <path d="M9.5 18V6l10-2v12"/><path d="M9.5 9l10-2"/>
  </>,
  // Cinema — a clapperboard.
  '🎬': <>
    <path d="M3 8.6h18V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <rect x="3" y="3.4" width="18" height="5.2" rx="1"/>
    <path d="M7.4 3.4L4.8 8.6M12 3.4L9.4 8.6M16.6 3.4L14 8.6"/>
  </>,
  '☕': <>
    <path d="M4 6h13v6.5A4.5 4.5 0 0 1 12.5 17h-4A4.5 4.5 0 0 1 4 12.5z"/>
    <path d="M17 8h1.8a2.2 2.2 0 0 1 0 4.4H17"/>
    <path d="M4 21h13"/>
  </>,
  '🏦': <>
    <path d="M3 9.5L12 4l9 5.5"/>
    <path d="M4.5 9.5V18M9.5 9.5V18M14.5 9.5V18M19.5 9.5V18"/>
    <path d="M3 20.5h18"/>
  </>,
  '✂️': <>
    <circle cx="6" cy="18" r="2.5"/><circle cx="6" cy="6" r="2.5"/>
    <path d="M8 16.5L19 4M8 7.5L19 20"/>
  </>,
  '🦷': <path d="M7.5 3.5C5.5 3.5 4 5.3 4 8c0 3.5 1 5 1.6 8.4.4 2.2.9 3.6 2 3.6 1.4 0 1.5-2.2 2-4.4.3-1.2.7-1.9 2.4-1.9s2.1.7 2.4 1.9c.5 2.2.6 4.4 2 4.4 1.1 0 1.6-1.4 2-3.6C19 13 20 11.5 20 8c0-2.7-1.5-4.5-3.5-4.5-1.6 0-2.6.8-4.5.8s-2.9-.8-4.5-.8z"/>,
  '🎁': <>
    <rect x="3" y="9" width="18" height="11" rx="1.5"/>
    <path d="M2 9h20"/><path d="M12 9v11"/>
    <path d="M12 9c-3.5 0-5-1-5-2.8S8.5 3 12 9zM12 9c3.5 0 5-1 5-2.8S15.5 3 12 9z"/>
  </>,
  // Vacanza — the parasol leaning, planted on the line of the sand.
  '🏖️': <>
    <path d="M8.4 8.4A7.6 7.6 0 0 1 21.6 4.3z"/>
    <path d="M15 6.3L10 20.4"/>
    <path d="M1.8 20.4h20.4"/>
  </>,
  '❤️': <path d="M12 20s-7-4.4-7-9.2A3.8 3.8 0 0 1 12 8.4 3.8 3.8 0 0 1 19 10.8C19 15.6 12 20 12 20z"/>,
  // Famiglia — the only one drawn solid, and for two reasons: "silhouette"
  // means exactly that, and it is the only way to put the small ones IN FRONT
  // of the big ones. Outlines would cross and become a tangle. The pale
  // outline around the children is the card's own colour, so it separates them
  // in the light theme and in the dark one alike.
  '👪': <>
    <g fill="currentColor" stroke="none">
      <circle cx="7" cy="5.2" r="2.6"/>
      <path d="M7 8.8a4.6 4.6 0 0 0-4.6 4.6V20h9.2v-6.6A4.6 4.6 0 0 0 7 8.8z"/>
      <circle cx="17" cy="5.2" r="2.6"/>
      <path d="M17 8.8a4.6 4.6 0 0 0-4.6 4.6V20h9.2v-6.6A4.6 4.6 0 0 0 17 8.8z"/>
    </g>
    <g fill="currentColor" stroke="var(--color-card)" strokeWidth="1.6" strokeLinejoin="round">
      <circle cx="9.2" cy="13.6" r="2.1"/>
      <path d="M9.2 16.4a3.6 3.6 0 0 0-3.6 3.6v1.6h7.2V20a3.6 3.6 0 0 0-3.6-3.6z"/>
      <circle cx="15.4" cy="13.6" r="2.1"/>
      <path d="M15.4 16.4a3.6 3.6 0 0 0-3.6 3.6v1.6h7.2V20a3.6 3.6 0 0 0-3.6-3.6z"/>
    </g>
  </>,
};

/**
 * The drawing for an icon. Returns null for anything it does not know, so a
 * title that happens to begin with some other emoji is left alone.
 */
export default function EventIcon({ icon, size = 18, style }) {
  const drawing = DRAW[icon] || DRAW[LEGACY[icon]];
  if (!drawing) return null;
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size}
      aria-hidden="true" focusable="false"
      stroke="currentColor" fill="none" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, verticalAlign: '-0.15em', ...style }}
    >
      {drawing}
    </svg>
  );
}
